import React, { useState, useEffect } from 'react'
import { t } from 'i18next'
import { Tab, type TabItem } from '@awell-health/design-system'
import { ErrorBoundary, ErrorPage } from '@awell/ui-kit/compositions/ErrorPage'
import { Activity, ActivityAction } from '../types'
import { ContextTab } from '../ContextTab'
import { UpdatesTab } from '../UpdatesTab'
import { useAgentActivityData } from './useAgentActivityData'
import { AgentDetailTab } from './tabs/AgentDetailTab'
import { ActivityDetailLoading } from '../ActivityDetailLoading'
import { SentTab } from './tabs/SentTab'
import { ReceivedTab } from './tabs/ReceivedTab'

interface Props {
  activity: Activity
}

export const AgentActivityDetails: React.FC<Props> = ({ activity }) => {
  const { orchestrationAgent, agentConfig, loading, error } = useAgentActivityData(activity)
  const [activeTab, setActiveTab] = useState<string>('agent')
  useEffect(() => {
    setActiveTab('agent')
  }, [activity.id])

  if (activity.context?.agent_thread_id && activity.context?.agent_config_id && error) {
    return (
      <ErrorPage
        showButton={false}
        labels={{
          errorTitle: t('error_load_agent_details'),
          errorContent: t('error_load_agent_details')
        }}
      />
    )
  }

  const tabs: TabItem[] = [
    {
      id: 'agent',
      label: 'Agent',
      onClick: () => setActiveTab('agent')
    },
    {
      id: 'details',
      label: 'Details',
      onClick: () => setActiveTab('details')
    },
    {
      id: 'updates',
      label: t('tab_title_updates', { count: activity.sub_activities.length }),
      onClick: () => setActiveTab('updates')
    }
  ]
  if (activity.action === ActivityAction.Activate) {
    tabs.splice(1, 0, {
      id: 'sent',
      label: 'Sent',
      onClick: () => setActiveTab('sent')
    })
  }
  if (activity.action === ActivityAction.Complete) {
    tabs.splice(1, 0, {
      id: 'received',
      label: 'Received',
      onClick: () => setActiveTab('received')
    })
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'agent':
        return <AgentDetailTab agentConfig={agentConfig} orchestrationAgent={orchestrationAgent} activity={activity} />
      case 'details':
        return <ContextTab activity={activity} />
      case 'updates':
        return <UpdatesTab activity={activity} />
      case 'sent':
        return <SentTab agentConfig={agentConfig} orchestrationAgent={orchestrationAgent} />
      case 'received':
        return <ReceivedTab agentConfig={agentConfig} orchestrationAgent={orchestrationAgent} />
    }
  }

  return (
    <ErrorBoundary panel showStack={true}>
      <div id="agent-activity-details">
        <div className="sticky top-0 bg-white z-10">
          <Tab variant="bordered" items={tabs} selected={activeTab} fullWidth={true} id="agent-activity-details-tab" />
        </div>
        <div className="p-4" aria-label={activeTab} role="tabpanel" id={`agent-activity-details-${activeTab}`}>
          {loading ? <ActivityDetailLoading /> : renderTabContent()}
        </div>
      </div>
    </ErrorBoundary>
  )
}
